/**
 * Payment Portal — Card/Currency Formatters & Utilities
 */

/**
 * Formats a raw card number string with spaces every 4 digits.
 * @param {string} value
 * @returns {string}
 */
export function formatCardNumber(value) {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Masks a card number, showing only the last 4 digits.
 * @param {string} value
 * @returns {string}
 */
export function maskCardNumber(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length < 4) return digits;
    const last4 = digits.slice(-4);
    const masked = '*'.repeat(digits.length - 4);
    return formatCardNumber(masked + last4);
}

/**
 * Formats an expiry string as MM/YY.
 * @param {string} value
 * @returns {string}
 */
export function formatExpiry(value) {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return digits;
}

/**
 * Formats a numeric amount as currency.
 * @param {number} amount
 * @param {string} [currency='USD']
 * @returns {string}
 */
export function formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(amount);
}

/**
 * Detects card brand from card number.
 * @param {string} number - Raw or formatted card number
 * @returns {'visa'|'mastercard'|'amex'|'discover'|'unknown'}
 */
export function detectCardBrand(number) {
    const n = number.replace(/\D/g, '');
    if (/^4/.test(n)) return 'visa';
    if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return 'mastercard';
    if (/^3[47]/.test(n)) return 'amex';
    if (/^6(?:011|5)/.test(n)) return 'discover';
    return 'unknown';
}

/**
 * Returns a display label for a card brand.
 * @param {'visa'|'mastercard'|'amex'|'discover'|'unknown'} brand
 * @returns {string}
 */
export function getCardBrandLabel(brand) {
    const labels = {
        visa: 'VISA',
        mastercard: 'Mastercard',
        amex: 'Amex',
        discover: 'Discover',
        unknown: '',
    };
    return labels[brand] ?? '';
}
